import React, { useState, useEffect } from "react";
import AdminHeader from "../../components/header";
import AdminSidebar from "../../components/sidebar";
import styles from "../../styles/admin.module.css";
import axiosInstance from "../../../AxiosInstance";
import { toast } from "react-toastify";

export default function WhatsAppCampaign() {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState("epicerie");
  const [selectedSegmentId, setSelectedSegmentId] = useState(null);
  const [daysThreshold, setDaysThreshold] = useState(30);
  const [message, setMessage] = useState("");
  const [recipientsCount, setRecipientsCount] = useState(0);
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("");

  // Nouvelles variables pour les templates
  const [templates, setTemplates] = useState([]);
  const [useTemplate, setUseTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateParameters, setTemplateParameters] = useState({});
  const [imageUrl, setImageUrl] = useState("");
  const [testUseTemplate, setTestUseTemplate] = useState(false);
  const [testSelectedTemplate, setTestSelectedTemplate] = useState("");
  const [testTemplateParameters, setTestTemplateParameters] = useState({});
  const [testImageUrl, setTestImageUrl] = useState("");

  // Types de segments disponibles
  const segmentTypes = [
    { value: "epicerie", label: "A déjà commandé dans l'épicerie" },
    { value: "has_orders", label: "A au moins une commande" },
    { value: "no_orders", label: "N'a jamais commandé" },
    {
      value: "last_order_days",
      label: "Dernière commande il y'a plus de X jours",
    },
  ];

  useEffect(() => {
    // Charger les segments existants
    loadSegments();
    // Charger les templates WhatsApp
    loadTemplates();
  }, []);

  // Charger les segments quand ils changent
  useEffect(() => {
    if (selectedSegmentId) {
      getRecipientsCount();
    }
  }, [selectedSegmentId]);

  const loadTemplates = async () => {
    try {
      const response = await axiosInstance.get("/whatsapp/templates/");
      setTemplates(response.data);
    } catch (error) {
      console.error("Erreur chargement templates:", error);
      toast.error("Erreur lors du chargement des templates WhatsApp");
    }
  };

  const loadSegments = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/segments/preview/");

      // Vérifier si la réponse est un tableau
      if (Array.isArray(response.data)) {
        setSegments(response.data);
      } else if (response.data && Array.isArray(response.data.results)) {
        // Si c'est une réponse paginée
        setSegments(response.data.results);
      } else {
        console.warn("Format de réponse inattendu:", response.data);
        setSegments([]);
      }

      // Si aucun segment n'existe, proposer de créer les segments par défaut
      if (Array.isArray(response.data) && response.data.length === 0) {
        toast.info(
          "Aucun segment trouvé. Cliquez sur 'Créer segments par défaut' pour commencer."
        );
      }
    } catch (error) {
      console.error("Erreur chargement segments:", error);
      toast.error("Erreur lors du chargement des segments");
      // En cas d'erreur, on met un tableau vide
      setSegments([]);
    } finally {
      setLoading(false);
    }
  };

  const getRecipientsCount = async () => {
    if (!selectedSegmentId) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/customers/segments/${selectedSegmentId}/clients/?count_only=true`
      );
      setRecipientsCount(response.data.count || 0);
    } catch (error) {
      console.error("Erreur comptage destinataires:", error);
      setRecipientsCount(0);
    } finally {
      setLoading(false);
    }
  };

  const sendTestMessage = async () => {
    if (!testPhone) {
      toast.error("Veuillez remplir le numéro de téléphone de test");
      return;
    }

    if (testUseTemplate && !testSelectedTemplate) {
      toast.error("Veuillez sélectionner un template");
      return;
    }

    if (!testUseTemplate && !testMessage) {
      toast.error("Veuillez remplir le message de test");
      return;
    }

    try {
      setSending(true);

      const payload = {
        phone_number: testPhone,
        segment_type: "test", // Pour indiquer que c'est un test
      };

      if (testUseTemplate) {
        payload.use_template = true;
        payload.template_name = testSelectedTemplate;
        if (Object.keys(testTemplateParameters).length > 0) {
          payload.template_parameters = Object.values(testTemplateParameters);
        }
        if (testImageUrl) {
          payload.image_url = testImageUrl;
        }
      } else {
        payload.message = testMessage;
      }

      const response = await axiosInstance.post(
        "/clients/send_whatsapp_bulk/",
        payload
      );

      if (response.status === 200) {
        toast.success("Message de test envoyé avec succès !");
        setTestMessage("");
        setTestTemplateParameters({});
        setTestImageUrl("");
      }
    } catch (error) {
      console.error("Erreur envoi test:", error);
      toast.error("Erreur lors de l'envoi du message de test");
    } finally {
      setSending(false);
    }
  };

  const sendBulkMessage = async () => {
    if (!selectedSegmentId) {
      toast.error("Veuillez sélectionner un segment");
      return;
    }

    if (useTemplate && !selectedTemplate) {
      toast.error("Veuillez sélectionner un template");
      return;
    }

    if (!useTemplate && !message.trim()) {
      toast.error("Veuillez saisir un message");
      return;
    }

    const confirmSend = window.confirm(
      `Êtes-vous sûr de vouloir envoyer ce message?`
    );

    if (!confirmSend) return;

    try {
      setSending(true);

      const payload = {
        segment_id: selectedSegmentId,
      };

      // Ajouter le nombre de jours si c'est un segment last_order_days
      if (selectedSegment === "last_order_days") {
        payload.days_threshold = daysThreshold;
      }

      if (useTemplate) {
        payload.use_template = true;
        payload.template_name = selectedTemplate;
        if (Object.keys(templateParameters).length > 0) {
          payload.template_parameters = Object.values(templateParameters);
        }
        if (imageUrl) {
          payload.image_url = imageUrl;
        }
      } else {
        payload.message = message;
      }

      const response = await axiosInstance.post(
        "/clients/send_whatsapp_bulk/",
        payload
      );

      if (response.status === 200) {
        const { sent, failed, total_recipients } = response.data;
        toast.success(
          `Campagne terminée ! ${sent} envoyés, ${failed} échecs sur ${total_recipients} destinataires`
        );
        setMessage("");
        setTemplateParameters({});
        setImageUrl("");
      }
    } catch (error) {
      console.error("Erreur envoi en masse:", error);
      toast.error("Erreur lors de l'envoi en masse");
    } finally {
      setSending(false);
    }
  };

  const handleTemplateParameterChange = (paramName, value, isTest = false) => {
    if (isTest) {
      setTestTemplateParameters((prev) => ({
        ...prev,
        [paramName]: value,
      }));
    } else {
      setTemplateParameters((prev) => ({
        ...prev,
        [paramName]: value,
      }));
    }
  };

  const getSelectedTemplateData = (isTest = false) => {
    const templateId = isTest ? testSelectedTemplate : selectedTemplate;
    return templates.find((t) => t.id === templateId);
  };

  const createDefaultSegments = async () => {
    try {
      setLoading(true);
      await axiosInstance.post("/customers/segments/create_defaults/");
      toast.success("Segments par défaut créés avec succès !");
      loadSegments(); // Recharger les segments
    } catch (error) {
      console.error("Erreur création segments:", error);
      toast.error("Erreur lors de la création des segments");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.adminContainer}>
      <AdminHeader />
      <div className={styles.adminContent}>
        <AdminSidebar />

        <div className={styles.mainContent}>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h1 className="h3 mb-0">Campagnes WhatsApp</h1>
                    <p className="text-muted mb-0">
                      Envoyez des messages WhatsApp en masse à vos clients
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              {/* Section Test */}
              <div className="col-lg-6 mb-4">
                <div className="card shadow-sm">
                  <div className="card-header bg-primary text-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-flask me-2"></i>
                      Test d'envoi
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        Numéro de téléphone de test :
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="221XXXXXXXXX"
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="testUseTemplate"
                          checked={testUseTemplate}
                          onChange={(e) => setTestUseTemplate(e.target.checked)}
                        />
                        <label
                          className="form-check-label fw-bold"
                          htmlFor="testUseTemplate"
                        >
                          Utiliser un template WhatsApp Meta
                        </label>
                      </div>
                    </div>

                    {testUseTemplate ? (
                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Sélectionner un template :
                        </label>
                        <select
                          className="form-select"
                          value={testSelectedTemplate}
                          onChange={(e) =>
                            setTestSelectedTemplate(e.target.value)
                          }
                        >
                          <option value="">Choisir un template...</option>
                          {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name} - {template.description}
                            </option>
                          ))}
                        </select>

                        {testSelectedTemplate &&
                          getSelectedTemplateData(true) && (
                            <div className="mt-2 p-3 bg-light rounded">
                              <small className="text-muted">
                                <strong>Template :</strong>{" "}
                                {getSelectedTemplateData(true).content}
                              </small>
                            </div>
                          )}

                        {testSelectedTemplate &&
                          getSelectedTemplateData(true)?.parameters &&
                          getSelectedTemplateData(true).parameters.length >
                            0 && (
                            <div className="mt-3">
                              <label className="form-label fw-bold small">
                                Paramètres du template :
                              </label>
                              {getSelectedTemplateData(true).parameters.map(
                                (param) => (
                                  <div key={param.name} className="mb-2">
                                    <label className="form-label small">
                                      {param.display_name}
                                      {param.required && (
                                        <span className="text-danger"> *</span>
                                      )}
                                    </label>
                                    <input
                                      type={
                                        param.type === "image" ? "url" : "text"
                                      }
                                      className="form-control form-control-sm"
                                      value={
                                        param.type === "image"
                                          ? testImageUrl
                                          : testTemplateParameters[
                                              param.name
                                            ] || ""
                                      }
                                      onChange={(e) => {
                                        if (param.type === "image") {
                                          setTestImageUrl(e.target.value);
                                        } else {
                                          handleTemplateParameterChange(
                                            param.name,
                                            e.target.value,
                                            true
                                          );
                                        }
                                      }}
                                      placeholder={
                                        param.type === "image"
                                          ? "https://example.com/image.jpg"
                                          : param.placeholder ||
                                            `Entrez ${param.display_name.toLowerCase()}`
                                      }
                                    />
                                  </div>
                                )
                              )}
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Message de test :
                        </label>
                        <textarea
                          className="form-control"
                          placeholder="Votre message de test..."
                          value={testMessage}
                          onChange={(e) => setTestMessage(e.target.value)}
                          rows={3}
                        />
                      </div>
                    )}
                    <button
                      onClick={sendTestMessage}
                      disabled={
                        sending ||
                        !testPhone ||
                        (testUseTemplate ? !testSelectedTemplate : !testMessage)
                      }
                      className="btn btn-primary"
                    >
                      {sending ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          ></span>
                          Envoi...
                        </>
                      ) : (
                        "Envoyer le test"
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Section Campagne */}
              <div className="col-lg-6 mb-4">
                <div className="card shadow-sm">
                  <div className="card-header bg-success text-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-bullhorn me-2"></i>
                      Nouvelle campagne
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        Sélectionner le segment :
                      </label>
                      <select
                        className="form-select"
                        value={selectedSegmentId || ""}
                        onChange={(e) => {
                          const segmentId = e.target.value;
                          setSelectedSegmentId(segmentId);
                          if (segmentId) {
                            const segment = segments.find(
                              (s) => s.id == segmentId
                            );
                            setSelectedSegment(segment?.type || "");
                          }
                        }}
                        onBlur={getRecipientsCount}
                      >
                        <option value="">Choisir un segment...</option>
                        {Array.isArray(segments) &&
                          segments.map((segment) => (
                            <option key={segment.id} value={segment.id}>
                              {segment.name} - {segment.description}
                            </option>
                          ))}
                      </select>
                    </div>

                    {/* Champ pour le nombre de jours si c'est un segment last_order_days */}
                    {selectedSegment === "last_order_days" && (
                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Nombre de jours depuis la dernière commande :
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={daysThreshold}
                          onChange={(e) =>
                            setDaysThreshold(parseInt(e.target.value) || 30)
                          }
                          min="1"
                          max="365"
                        />
                        <div className="form-text">
                          <small>
                            Clients qui n'ont pas commandé depuis plus de{" "}
                            {daysThreshold} jours
                          </small>
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="useTemplate"
                          checked={useTemplate}
                          onChange={(e) => setUseTemplate(e.target.checked)}
                        />
                        <label
                          className="form-check-label fw-bold"
                          htmlFor="useTemplate"
                        >
                          Utiliser un template WhatsApp Meta
                        </label>
                      </div>
                    </div>

                    {useTemplate ? (
                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Sélectionner un template :
                        </label>
                        <select
                          className="form-select"
                          value={selectedTemplate}
                          onChange={(e) => setSelectedTemplate(e.target.value)}
                        >
                          <option value="">Choisir un template...</option>
                          {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name} - {template.description}
                            </option>
                          ))}
                        </select>

                        {selectedTemplate && getSelectedTemplateData(false) && (
                          <div className="mt-2 p-3 bg-light rounded">
                            <small className="text-muted">
                              <strong>Template :</strong>{" "}
                              {getSelectedTemplateData(false).content}
                            </small>
                          </div>
                        )}

                        {selectedTemplate &&
                          getSelectedTemplateData(false)?.parameters &&
                          getSelectedTemplateData(false).parameters.length >
                            0 && (
                            <div className="mt-3">
                              <label className="form-label fw-bold small">
                                Paramètres du template :
                              </label>
                              {getSelectedTemplateData(false).parameters.map(
                                (param) => (
                                  <div key={param.name} className="mb-2">
                                    <label className="form-label small">
                                      {param.display_name}
                                      {param.required && (
                                        <span className="text-danger"> *</span>
                                      )}
                                    </label>
                                    <input
                                      type={
                                        param.type === "image" ? "url" : "text"
                                      }
                                      className="form-control form-control-sm"
                                      value={
                                        param.type === "image"
                                          ? imageUrl
                                          : templateParameters[param.name] || ""
                                      }
                                      onChange={(e) => {
                                        if (param.type === "image") {
                                          setImageUrl(e.target.value);
                                        } else {
                                          handleTemplateParameterChange(
                                            param.name,
                                            e.target.value,
                                            false
                                          );
                                        }
                                      }}
                                      placeholder={
                                        param.type === "image"
                                          ? "https://example.com/image.jpg"
                                          : param.placeholder ||
                                            `Entrez ${param.display_name.toLowerCase()}`
                                      }
                                    />
                                  </div>
                                )
                              )}
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="mb-3">
                        <label className="form-label fw-bold">Message :</label>
                        <textarea
                          className="form-control"
                          placeholder="Votre message... (utilisez {{nom}}, {{prenom}} pour personnaliser)"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          rows={6}
                        />
                        <div className="form-text">
                          <small>
                            Variables disponibles : {"{{nom}}"}, {"{{prenom}}"},{" "}
                            {"{{nom_complet}}"}, {"{{telephone}}"},{" "}
                            {"{{email}}"}, {"{{points}}"}, {"{{product_name}}"},{" "}
                            {"{{promo_price}}"}
                          </small>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={sendBulkMessage}
                      disabled={
                        sending ||
                        (useTemplate ? !selectedTemplate : !message.trim())
                      }
                      className="btn btn-success btn-lg"
                    >
                      {sending ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          ></span>
                          Envoi en cours...
                        </>
                      ) : (
                        "Envoyer la campagne"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Section Segments existants */}
            <div className="row">
              <div className="col-12">
                <div className="card shadow-sm">
                  <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-list me-2"></i>
                      Segments existants
                    </h5>
                    <button
                      className="btn btn-sm btn-light"
                      onClick={createDefaultSegments}
                      disabled={loading}
                    >
                      <i className="fas fa-plus me-1"></i>
                      Créer segments par défaut
                    </button>
                  </div>
                  <div className="card-body">
                    {loading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">Chargement...</span>
                        </div>
                        <p className="mt-2">Chargement des segments...</p>
                      </div>
                    ) : (
                      <div className="row">
                        {Array.isArray(segments) && segments.length > 0 ? (
                          segments.map((segment) => (
                            <div
                              key={segment.id}
                              className="col-md-6 col-lg-4 mb-3"
                            >
                              <div className="card border">
                                <div className="card-body">
                                  <h6 className="card-title text-primary">
                                    {segment.name}
                                  </h6>
                                  <p className="card-text text-muted small">
                                    {segment.description}
                                  </p>
                                  <div className="d-flex flex-wrap gap-2">
                                    <span className="badge bg-primary">
                                      {segment.type}
                                    </span>
                                    <span className="badge bg-success">
                                      {segment.clients_count} clients
                                    </span>
                                    <span
                                      className={`badge ${
                                        segment.is_active
                                          ? "bg-success"
                                          : "bg-secondary"
                                      }`}
                                    >
                                      {segment.is_active ? "Actif" : "Inactif"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="col-12 text-center py-4">
                            <p className="text-muted">
                              Aucun segment disponible
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
